import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Avatar, Chip, Divider,
  CircularProgress, Alert, IconButton, Tooltip, List,
  ListItem, ListItemAvatar, ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import useSWR from 'swr';
import { axiosService, fetcher } from 'shared/helpers/axios';

export default function PendingInvitationsDialog({ open, onClose, consortium }) {
  const consortiumId = consortium?.id;

  const { data, error, isLoading, mutate } = useSWR(
    open && consortiumId
      ? `/interact/consortium-invitations/?direction=sent&status=pending&consortium=${consortiumId}&page_size=100`
      : null,
    fetcher
  );

  const [cancelling, setCancelling] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  const invitations = data?.results ?? (Array.isArray(data) ? data : []);

  const registered = invitations.filter(
    (inv) => inv.invitation_info?.to_user && inv.invitation_info.to_user !== inv.invitation_info.to_email
  );
  const unregistered = invitations.filter(
    (inv) => !inv.invitation_info?.to_user || inv.invitation_info.to_user === inv.invitation_info?.to_email
  );

  const handleCancel = async (invId) => {
    setCancelling(invId);
    setCancelError(null);
    try {
      await axiosService.patch(`/interact/consortium-invitations/${invId}/`, { status: 'rejected' });
      mutate();
    } catch (e) {
      setCancelError('Failed to cancel invitation. Please try again.');
    } finally {
      setCancelling(null);
    }
  };

  const renderRow = (inv) => {
    const info = inv.invitation_info || {};
    const isEmail = !info.to_user || info.to_user === info.to_email;
    const label = isEmail ? info.to_user : info.to_user;
    const isCancellingThis = cancelling === inv.id;

    return (
      <ListItem
        key={inv.id}
        divider
        secondaryAction={
          <Tooltip title="Cancel invitation">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!!cancelling}
                onClick={() => handleCancel(inv.id)}
              >
                {isCancellingThis ? <CircularProgress size={16} color="error" /> : <CancelIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <ListItemAvatar>
          <Avatar sx={{ width: 36, height: 36, bgcolor: isEmail ? 'warning.main' : 'primary.main' }}>
            {isEmail ? <EmailIcon fontSize="small" /> : (label?.[0]?.toUpperCase() || <PersonIcon fontSize="small" />)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>{label || '—'}</Typography>
              <Chip
                label={isEmail ? 'Unregistered' : 'Registered'}
                size="small"
                variant="outlined"
                color={isEmail ? 'warning' : 'primary'}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              Invited by {info.from_user || 'you'} · {inv.created ? formatDistanceToNow(new Date(inv.created), { addSuffix: true }) : ''}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{zIndex:1600, height: '95vh'}}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          Pending Invitations
          {invitations.length > 0 && (
            <Chip label={invitations.length} size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load invitations.</Alert>
        )}

        {cancelError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setCancelError(null)}>{cancelError}</Alert>
        )}

        {!isLoading && !error && invitations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">No pending invitations.</Typography>
          </Box>
        )}

        {registered.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                Registered Users ({registered.length})
              </Typography>
            </Box>
            <List disablePadding>
              {registered.map(renderRow)}
            </List>
          </>
        )}

        {unregistered.length > 0 && (
          <>
            {registered.length > 0 && <Divider />}
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                Unregistered (email) ({unregistered.length})
              </Typography>
            </Box>
            <List disablePadding>
              {unregistered.map(renderRow)}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
